/**
 * The avatar and its one-item disclosure (mission 6 WP05 T032; A.5, FR-003,
 * FR-027, C-008; research §1.5, §1.8; style guide §7.17).
 *
 * A 28 px circle with the legal name's initials; its accessible name is
 * register 18's `avatarName`. One identity per tenant (C-008): no Owner ring,
 * no role text. The panel is a disclosure, not a `menu` (no arrow-key model to
 * honour): `aria-expanded` and `aria-controls` on the trigger, Esc closes it
 * and returns focus, a pointer-down outside closes it without moving focus.
 *
 * Sign out: `logout()`, then `queryClient.clear()`, then `/login`, in that
 * order. `clear()` drops every cached query, the tenant's included, which
 * discharges AuthGate's logout obligation (`clearTenantQuery`): the invert gate
 * on /login reads a fresh answer instead of the old session. A failed logout
 * leaves the cache and the route alone and shows register 16's
 * `genericError`, never the error's own text.
 */
import { useEffect, useId, useRef, useState } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { useNavigate } from 'react-router-dom'

import { useTenant } from '@/components/trellis/AuthGate'
import { Button } from '@/components/ui/button'
import { Skeleton } from '@/components/ui/skeleton'
import { logout } from '@/lib/auth'
import { copyText, PROVISIONAL_COPY } from '@/lib/provisional-copy'

/** The first letter of each of the first two words, upper-cased. */
function initials(legalName: string): string {
  return legalName
    .split(/\s+/)
    .filter(Boolean)
    .slice(0, 2)
    .map((word) => word.charAt(0).toUpperCase())
    .join('')
}

export function AvatarMenu() {
  const { data: tenant } = useTenant()
  const queryClient = useQueryClient()
  const navigate = useNavigate()
  const panelId = useId()
  const rootRef = useRef<HTMLDivElement>(null)
  const triggerRef = useRef<HTMLButtonElement>(null)
  const [open, setOpen] = useState(false)
  const [signingOut, setSigningOut] = useState(false)
  const [failed, setFailed] = useState(false)

  useEffect(() => {
    if (!open) return
    function onKeyDown(event: KeyboardEvent) {
      if (event.key !== 'Escape') return
      setOpen(false)
      triggerRef.current?.focus()
    }
    function onPointerDown(event: PointerEvent) {
      if (!rootRef.current?.contains(event.target as Node)) setOpen(false)
    }
    document.addEventListener('keydown', onKeyDown)
    document.addEventListener('pointerdown', onPointerDown)
    return () => {
      document.removeEventListener('keydown', onKeyDown)
      document.removeEventListener('pointerdown', onPointerDown)
    }
  }, [open])

  if (!tenant) return <Skeleton className="size-7 rounded-full" />

  async function signOut() {
    setSigningOut(true)
    setFailed(false)
    try {
      await logout()
    } catch {
      setSigningOut(false)
      setFailed(true)
      return
    }
    queryClient.clear()
    navigate('/login', { replace: true })
  }

  return (
    <div ref={rootRef} className="relative">
      <button
        ref={triggerRef}
        type="button"
        aria-expanded={open}
        aria-controls={panelId}
        aria-label={PROVISIONAL_COPY.avatarName.render({ email: tenant.contact_email })}
        onClick={() => setOpen((o) => !o)}
        className="flex size-7 items-center justify-center rounded-full bg-navy-200 text-caption font-semibold text-navy-950 focus-visible:outline-none focus-visible:[box-shadow:var(--focus-ring)]"
      >
        {initials(tenant.legal_name)}
      </button>
      <div
        id={panelId}
        hidden={!open}
        className="absolute right-0 z-20 mt-2 min-w-40 rounded-md border border-navy-200 bg-white p-1 [box-shadow:var(--shadow-sm)]"
      >
        <Button variant="ghost" className="w-full justify-start" loading={signingOut} onClick={() => void signOut()}>
          {copyText('signOut')}
        </Button>
        {failed ? (
          <p role="alert" className="px-3 py-2 text-caption text-[var(--color-error-700)]">
            {copyText('genericError')}
          </p>
        ) : null}
      </div>
    </div>
  )
}
